/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { cloneDeep } from 'lodash'

import type { DeploymentTemplateInfoV2 } from '@modules/cloud-api/v1/types'
import type { RegionId, VersionNumber } from '@modules/ui-types'

import { getSliderPlan } from '@/lib/stackDeployments/selectors/stackDeployment'

import { FETCH_DEPLOYMENT_TEMPLATE, FETCH_DEPLOYMENT_TEMPLATES } from '../constants/actions'
import { replaceIn } from '../lib/immutability-helpers'

import type { AsyncAction, ReduxState } from '@/types/redux'

export interface State {
  [regionId: string]: DeploymentTemplateInfoV2[] | undefined
}

interface FetchAllAction
  extends AsyncAction<typeof FETCH_DEPLOYMENT_TEMPLATES, DeploymentTemplateInfoV2[]> {
  meta: {
    regionId: RegionId
    stackVersion?: VersionNumber
  }
}

interface FetchSingleAction
  extends AsyncAction<typeof FETCH_DEPLOYMENT_TEMPLATE, DeploymentTemplateInfoV2> {
  meta: {
    regionId: RegionId
    templateId: string
    stackVersion?: VersionNumber
  }
}

function makeDeploymentTemplatesDescriptor(
  regionId: string,
  stackVersion: VersionNumber | null | undefined,
): string {
  const versionString = stackVersion != null ? stackVersion : `unversioned`
  return `${regionId}/${versionString}`
}

type Action = FetchAllAction | FetchSingleAction

export default function deploymentTemplatesReducer(state: State = {}, action: Action): State {
  if (action.type === FETCH_DEPLOYMENT_TEMPLATES) {
    if (!action.error && action.payload) {
      const { regionId, stackVersion } = action.meta
      const templates = action.payload.map(normalizeDeploymentTemplate)

      return replaceIn(state, makeDeploymentTemplatesDescriptor(regionId, stackVersion), templates)
    }
  }

  if (action.type === FETCH_DEPLOYMENT_TEMPLATE) {
    if (!action.error && action.payload) {
      const { regionId, templateId, stackVersion } = action.meta
      const template = normalizeDeploymentTemplate(action.payload)
      const descriptor = makeDeploymentTemplatesDescriptor(regionId, stackVersion)
      const currentState = state[descriptor]

      // Have we already loaded templates for this region+version?
      if (currentState) {
        const currentIndex = currentState.findIndex((t) => t.id === templateId)

        // Does the current data already contain this template?
        if (currentIndex < 0) {
          // It doesn't, so copy the existing state and insert the template
          return {
            ...state,
            [descriptor]: [...currentState, template],
          }
        }

        // Copy the state and overwrite the existing value
        const newState = [...currentState]
        newState[currentIndex] = template
        return {
          ...state,
          [descriptor]: newState,
        }
      }

      // No state for this region+version yet
      return {
        ...state,
        [descriptor]: [template],
      }
    }
  }

  return state
}

export function _getDeploymentTemplates(
  state: State,
  regionId: RegionId,
  stackVersion: VersionNumber | null,
): DeploymentTemplateInfoV2[] | undefined {
  const templates = state[makeDeploymentTemplatesDescriptor(regionId, stackVersion)]

  if (!Array.isArray(templates)) {
    return templates
  }

  return templates.sort(defaultTemplateFirst)
}

export function _getDeploymentTemplate(
  state: State,
  regionId: RegionId,
  templateId: string,
  stackVersion: VersionNumber | null,
): DeploymentTemplateInfoV2 | undefined {
  const templates = _getDeploymentTemplates(state, regionId, stackVersion)

  if (!Array.isArray(templates)) {
    return templates
  }

  return templates.find((template) => template.id === templateId)
}

function defaultTemplateFirst(t1: DeploymentTemplateInfoV2, t2: DeploymentTemplateInfoV2) {
  if (t1.id === 'default' && t2.id !== 'default') {
    return -1
  }

  if (t1.id !== 'default' && t2.id === 'default') {
    return 1
  }

  return t1.id.localeCompare(t2.id)
}

/* This function makes one necessary change. Typically we don't alter the API
 * payload at the reducer level but in this case some templates' Kibana plans
 * carry a legacy, plan-level zone_count instead of correctly placing that field
 * within the topology elements. This is unsupported in swagger-generated types
 * and therefore we do the transform here.
 */
function normalizeDeploymentTemplate(template: DeploymentTemplateInfoV2): DeploymentTemplateInfoV2 {
  const normalizedTemplate = cloneDeep(template)

  const kibanaPlan = getSliderPlan({
    deployment: normalizedTemplate.deployment_template,
    sliderInstanceType: `kibana`,
  })

  if (!kibanaPlan) {
    return normalizedTemplate
  }

  // @ts-ignore
  if (kibanaPlan.zone_count) {
    kibanaPlan.cluster_topology?.forEach((topologyElement) => {
      // @ts-ignore
      topologyElement.zone_count = kibanaPlan.zone_count
    })
    // @ts-ignore
    delete kibanaPlan.zone_count
  }

  return normalizedTemplate
}

export const getDeploymentTemplate = (
  state: ReduxState,
  regionId: RegionId,
  templateId: string,
  stackVersion: VersionNumber | null = null,
) => _getDeploymentTemplate(state.deploymentTemplates, regionId, templateId, stackVersion)

export const getDeploymentTemplates = (
  state: ReduxState,
  regionId: RegionId,
  stackVersion: VersionNumber | null,
) => _getDeploymentTemplates(state.deploymentTemplates, regionId, stackVersion)
